import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

import type { WorkItem } from "./homebase/unifiedWorkItems";
import { CommandCenterEmptyState } from "./command-center/CommandCenterEmptyState";
import {
  FilterBar,
  type CommandCenterView,
  type FilterBarWorkspace,
} from "./command-center/FilterBar";
import { TaskBoardView } from "./command-center/TaskBoardView";
import { CommandCenterMetrics, TaskControlView } from "./command-center/TaskControlView";
import {
  TaskDetailPanel,
  type DependencySummary,
  type TaskDetailThreadSummary,
  type TaskMutationPatch,
} from "./command-center/TaskDetailPanel";
import { CommandCenterTaskSurfaceSkeleton } from "./command-center/CommandCenterTaskSurfaceSkeleton";
import type {
  CommandCenterEmptyStateContent,
  CommandCenterOption,
} from "./NoActiveThreadState.logic";

export interface NoActiveThreadCommandCenterSurfaceProps {
  readonly workspaces: readonly FilterBarWorkspace[];
  readonly activeWorkspaceId: string | null;
  readonly searchQuery: string;
  readonly readyOnly: boolean;
  readonly readyCount: number;
  readonly teamFilter: string;
  readonly teamOptions: ReadonlyArray<CommandCenterOption>;
  readonly campaignFilter: string;
  readonly campaignOptions: ReadonlyArray<CommandCenterOption>;
  readonly projectFilter: string;
  readonly projectOptions: ReadonlyArray<CommandCenterOption>;
  readonly statusFilter: CmmdTaskStatus | "all";
  readonly assigneeFilter: string;
  readonly assigneeOptions: ReadonlyArray<CommandCenterOption>;
  readonly isRefreshing: boolean;
  readonly view: CommandCenterView;
  readonly isLoading: boolean;
  readonly items: readonly WorkItem[];
  readonly selectedItemId: string | null;
  readonly selectedItem: WorkItem | null;
  readonly selectedAssigneeId: string | null;
  readonly selectedDescription: string | null;
  readonly selectedTaskThreads: readonly TaskDetailThreadSummary[];
  readonly emptyStateContent: CommandCenterEmptyStateContent;
  readonly hasActiveFilters: boolean;
  readonly resolveDependency: (id: string) => DependencySummary | null;
  readonly onViewChange: (view: CommandCenterView) => void;
  readonly onRefresh: () => void;
  readonly onWorkspaceChange: (workspaceId: string | null) => void;
  readonly onSearchQueryChange: (value: string) => void;
  readonly onReadyOnlyChange: (next: boolean) => void;
  readonly onTeamFilterChange: (value: string) => void;
  readonly onCampaignFilterChange: (value: string) => void;
  readonly onProjectFilterChange: (value: string) => void;
  readonly onStatusFilterChange: (value: CmmdTaskStatus | "all") => void;
  readonly onAssigneeFilterChange: (value: string) => void;
  readonly onClearFilters: () => void;
  readonly onSelect: (id: string) => void;
  readonly onUpdateTaskStatus: (itemId: string, status: CmmdTaskStatus) => void | Promise<void>;
  readonly onCreateTask: (input: { title: string; status: CmmdTaskStatus }) => void;
  readonly onDeleteTask: (itemId: string) => void;
  readonly onNavigateToDependency: (id: string) => void;
  readonly onCloseTaskDetail: () => void;
  readonly onStartThread: (itemId: string) => void | Promise<void>;
  readonly onUpdateTask: (itemId: string, patch: TaskMutationPatch) => void | Promise<void>;
}

export function NoActiveThreadCommandCenterSurface(props: NoActiveThreadCommandCenterSurfaceProps) {
  // Destructured for the chrome this component renders itself; `props` stays
  // whole for the panes below, which take the same shape.
  const {
    workspaces,
    activeWorkspaceId,
    searchQuery,
    readyOnly,
    readyCount,
    teamFilter,
    teamOptions,
    campaignFilter,
    campaignOptions,
    projectFilter,
    projectOptions,
    statusFilter,
    assigneeFilter,
    assigneeOptions,
    isRefreshing,
    view,
    selectedItem,
    selectedAssigneeId,
    selectedDescription,
    selectedTaskThreads,
    resolveDependency,
    onViewChange,
    onRefresh,
    onWorkspaceChange,
    onSearchQueryChange,
    onReadyOnlyChange,
    onTeamFilterChange,
    onCampaignFilterChange,
    onProjectFilterChange,
    onStatusFilterChange,
    onAssigneeFilterChange,
    onNavigateToDependency,
    onCloseTaskDetail,
    onStartThread,
    onUpdateTask,
  } = props;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <FilterBar
        workspaces={[...workspaces]}
        activeWorkspaceId={activeWorkspaceId}
        searchQuery={searchQuery}
        readyOnly={readyOnly}
        readyCount={readyCount}
        teamFilter={teamFilter}
        teamOptions={teamOptions}
        campaignFilter={campaignFilter}
        campaignOptions={campaignOptions}
        projectFilter={projectFilter}
        projectOptions={projectOptions}
        statusFilter={statusFilter}
        assigneeFilter={assigneeFilter}
        assigneeOptions={assigneeOptions}
        isRefreshing={isRefreshing}
        view={view}
        onViewChange={onViewChange}
        onRefresh={onRefresh}
        onWorkspaceChange={onWorkspaceChange}
        onSearchQueryChange={onSearchQueryChange}
        onReadyOnlyChange={onReadyOnlyChange}
        onTeamFilterChange={onTeamFilterChange}
        onCampaignFilterChange={onCampaignFilterChange}
        onProjectFilterChange={onProjectFilterChange}
        onStatusFilterChange={onStatusFilterChange}
        onAssigneeFilterChange={onAssigneeFilterChange}
      />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <CommandCenterMainPane {...props} />
        <TaskDetailPanel
          item={selectedItem}
          assigneeId={selectedAssigneeId}
          description={selectedDescription}
          relatedThreads={selectedTaskThreads}
          resolveDependency={resolveDependency}
          onNavigateToDependency={onNavigateToDependency}
          onClose={onCloseTaskDetail}
          onStartThread={onStartThread}
          onUpdateTask={onUpdateTask}
        />
      </div>
    </div>
  );
}

function CommandCenterMainPane(props: NoActiveThreadCommandCenterSurfaceProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      {props.isLoading ? (
        <CommandCenterTaskSurfaceSkeleton />
      ) : props.items.length === 0 ? (
        <CommandCenterEmptyState
          title={props.emptyStateContent.title}
          description={props.emptyStateContent.description}
          actionLabel={props.emptyStateContent.actionLabel}
          onAction={props.hasActiveFilters ? props.onClearFilters : props.onRefresh}
        />
      ) : (
        <CommandCenterPopulatedView {...props} />
      )}
    </div>
  );
}

function CommandCenterPopulatedView(props: NoActiveThreadCommandCenterSurfaceProps) {
  return (
    <>
      <div className="px-4 pt-2 pb-3 sm:px-5">
        <CommandCenterMetrics items={props.items} />
      </div>
      {props.view === "control" ? (
        <TaskControlView
          items={props.items}
          selectedId={props.selectedItemId}
          onSelect={props.onSelect}
        />
      ) : (
        <TaskBoardView
          items={props.items}
          selectedId={props.selectedItemId}
          onSelect={props.onSelect}
          onUpdateTaskStatus={props.onUpdateTaskStatus}
          onCreateTask={props.onCreateTask}
          onDeleteTask={props.onDeleteTask}
        />
      )}
    </>
  );
}
